import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useFieldInfoLabelsContext } from "@/contexts/FieldInfoLabelsContext";

export type AppTranslationOptions = {
	useFieldInfo?: boolean;
	fieldInfoKeys?: string[];
	fieldInfoJoin?: string;
	[key: string]: unknown;
};

/**
 * `t` from {@link useAppTranslation}. Use for Zod schemas and other helpers
 * that need a plain function type — i18next's branded `TFunction` is not assignable here.
 */
export type AppTranslateFn = (
	key: string,
	options?: AppTranslationOptions,
) => string;

/**
 * Same as useTranslation() but t() supports field-info labels:
 * - t("EntityType.PropertyName", { useFieldInfo: true }) → label from API map
 * - t("...", { useFieldInfo: true, fieldInfoKeys: ["Org.Name", "User.Email"], fieldInfoJoin: " and " }) → joined labels
 * - Otherwise forwards to i18n t() (interpolation, defaultValue, etc. unchanged).
 *
 * Field-info fallback: when `useFieldInfo: true` is set and the API map does not
 * have a value for a key (missing, still loading, or returned empty/null), the
 * hook falls back to `i18nT(key, options)` so the local `public/locales/en/translation.json`
 * value (or the key/defaultValue) is shown instead of an empty string.
 */
export function useAppTranslation() {
	const { t: i18nT, ...rest } = useTranslation();
	const { getFieldLabel } = useFieldInfoLabelsContext();

	const t: AppTranslateFn = useCallback(
		(key, options) => {
			const i18nFallback = (k: string) =>
				(i18nT as (k: string, o?: Record<string, unknown>) => string)(
					k,
					options,
				);

			if (options?.useFieldInfo === true) {
				const resolveFieldInfo = (k: string) => {
					const label = getFieldLabel(k);
					if (typeof label === "string" && label.length > 0) return label;
					return i18nFallback(k);
				};

				if (
					options.fieldInfoKeys &&
					Array.isArray(options.fieldInfoKeys) &&
					options.fieldInfoKeys.length > 0
				) {
					const join = options.fieldInfoJoin ?? " and ";
					return options.fieldInfoKeys.map(resolveFieldInfo).join(join);
				}
				return resolveFieldInfo(key);
			}
			return i18nFallback(key);
		},
		[i18nT, getFieldLabel],
	);

	return { t, ...rest };
}
